import { Button, Card, Row } from "antd";
import { CustomModal } from "../../lib/CustomModal";
import { PhoneOutlined, MailOutlined } from "@ant-design/icons";
import { useCallback, useMemo, useState } from "react";
import AuthService from "../../services/auth";
import { useSelector } from "react-redux";
import { useGetRole } from "../../lib/hooks/getRole";
import MetaService from "../../services/meta";
import { TermsAndConditionsModal } from "../TermsAndConditionsModal";
import "./styles.scss";

type HelpModalProps = {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
};

export const HelpModal = ({ isModalOpen, setIsModalOpen }: HelpModalProps) => {
  const metaData = useSelector((state: any) => state.metaData);

  const [termsTabId, setTermsTabId] = useState<undefined | "1" | "2">(
    undefined
  );

  const onCloseTerms = useCallback(() => setTermsTabId(undefined), []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, isAdmin] = useGetRole();

  const data = useMemo(() => {
    const {
      companyName,
      noteserveName,
      companySupportEmail,
      noteserveSupportEmail,
      companySupportPhone,
      noteserveSupportPhone,
    } = metaData;

    const companyInfo = {
      name: companyName,
      email: companySupportEmail,
      phone: companySupportPhone,
      logo: MetaService.logoUrl(false),
    };

    if (!AuthService.isLoggedIn()) {
      return companyInfo;
    }

    return isAdmin
      ? {
          name: noteserveName,
          email: noteserveSupportEmail,
          phone: noteserveSupportPhone,
          logo: MetaService.logoUrl(),
        }
      : companyInfo;
  }, [isAdmin, metaData]);

  return (
    <CustomModal
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      titleText="We're here to assist"
      width={450}
      destroyOnClose
    >
      <div className="mt-6 mr-3 mb-3 ml-3">
        <img
          src={data.logo}
          alt="noteserve logo"
          className="logo-cursor"
          style={{ height: "24px" }}
        />
        <p className="text w-500 mt-7 mb-4">{data.name} support contacts</p>
        <Row>
          <Card style={{ width: "100%" }} className="mb-6">
            <h5 className="h h5 mb-4">Call Us</h5>
            <div className="mb-8">
              <a href={`tel:${data.phone?.substring(1)}`} className="card-link">
                <div className="d-flex align-center">
                  <PhoneOutlined className="contact-icon mr-4" />
                  <h4 className="h">{data.phone}</h4>
                </div>
              </a>
            </div>
            <p className="my-0 card-hint">
              Available Monday–Friday: 9AM–4PM (MDT)
            </p>
          </Card>

          <Card style={{ width: "100%" }}>
            <h5 className="h h5 mb-4">Email Us</h5>
            <div className="mb-8">
              <a href={`mailto:${data.email}`} className="card-link underline">
                <div className="d-flex align-center">
                  <MailOutlined className="contact-icon mr-4" />
                  <h4 className="h">{data.email}</h4>
                </div>
              </a>
            </div>
            <p className="my-0 card-hint">
              Respond Monday–Friday within 24–48 hours
            </p>
          </Card>

          <Row className="align-center w-100 justify-center mt-6">
            <Button
              type="link"
              className="pl-2 pr-0 py-0 text title"
              onClick={() => setTermsTabId("1")}
            >
              Terms of Use
            </Button>
            <div className="elipse ml-2" />
            <Button
              type="link"
              className="px-2 py-0 text title"
              onClick={() => setTermsTabId("2")}
            >
              Privacy Policy
            </Button>
          </Row>

          <TermsAndConditionsModal
            termsTabId={termsTabId}
            setTermsTabId={setTermsTabId}
            onClose={onCloseTerms}
          />
        </Row>
      </div>
    </CustomModal>
  );
};
